import { IconShieldFilled } from "@tabler/icons-react"
import { type FC, useLayoutEffect, useState } from "react"
import styled from "styled-components"

import { type CommunityPostReactionType } from "@forento/core/models/community"
import { toDateTimeString } from "@forento/core/utilities/date"
import { toCountFormat } from "@forento/core/utilities/number"
import { exhaustiveGuard } from "@forento/core/utilities/switch"
import CommunityCommentComposer from "@forento/ui/components/CommunityCommentComposer"
import CommunityCommentThreads from "@forento/ui/components/CommunityCommentThreads"
import CommunityPostReactionPicker from "@forento/ui/components/CommunityPostReactionPicker"
import CommunityPostReactionSummary from "@forento/ui/components/CommunityPostReactionSummary"
import CommunityProfileAvatar from "@forento/ui/components/CommunityProfileAvatar"
import LexicalViewer from "@forento/ui/components/LexicalViewer"
import { VimeoPlayer, YoutubePlayer } from "@forento/ui/components/VideoPlayer"

import Button from "~/components/Button"
import { CloseIcon, CommentIcon, LikeIcon } from "~/components/Icon"
import { DefaultModal, ModalHeader, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import PostReactions from "~/components/PostReactions"
import { useAlert } from "~/contexts/AlertContext"
import { toRelativeDateString } from "~/utilities/date"
import { fonts } from "~/utilities/styles"
import trpc, { query } from "~/utilities/trpc"

import Attachment from "../Post/Attachment"
import PollOption from "../Post/PollOption"
import useImageCarousel from "../Post/useImageCarousel"
import Comment from "./Comment"

type Props = { postId: number | null; onClose(): void }

const PostDetail: FC<Props> = ({ postId, onClose }) => {
	const alert = useAlert()

	const [lastPostId, setLastPostId] = useState<number | null>(null)
	const [comment, setComment] = useState("")
	const [isReactionsModalOpen, setReactionsModalOpen] = useState(false)

	const post = query.community.getPersonalizedPost.useQuery(lastPostId!, { enabled: lastPostId !== null })
	const comments = query.community.listPostComments.useQuery(lastPostId!, { enabled: lastPostId !== null })

	const createComment = query.community.createPostComment.useMutation({
		onSuccess: result => {
			if (result.moderationStatus === "pending")
				void alert.show(
					"Awaiting approval",
					"Your comment has been submitted for review and will appear once approved.",
				)
			comments.refetch()
			setComment("")
		},
	})
	const setReaction = query.community.setPostReaction.useMutation({ onSuccess: () => post.refetch() })
	const deleteReaction = query.community.deletePostReaction.useMutation({ onSuccess: () => post.refetch() })
	const setCommentReaction = query.community.setCommentReaction.useMutation({ onSuccess: () => comments.refetch() })
	const deleteCommentReaction = query.community.deleteCommentReaction.useMutation({
		onSuccess: () => comments.refetch(),
	})

	useLayoutEffect(() => {
		if (postId !== null) {
			setLastPostId(postId)
		}
	}, [postId])

	const handleSubmitComment = async () => {
		if (post.data == null || comment.trim().length === 0) return
		await createComment.mutateAsync({ postId: post.data.id, text: comment.trim() })
	}

	const handleReact = (type: CommunityPostReactionType | null) => {
		if (post.data == null) return
		if (type !== null) {
			setReaction.mutate({ postId: post.data.id, type })
		} else {
			deleteReaction.mutate(post.data.id)
		}
	}

	async function handleVote(pollOptionId: number | null) {
		if (post.data == null) return
		await trpc.community.setPostVote.mutate({
			postId: post.data.id,
			pollOptionId:
				pollOptionId !== null && !post.data.pollOptions.find(x => x.id === pollOptionId)?.hasUserSelected
					? pollOptionId
					: null,
		})
		await post.refetch()
	}

	async function handleDeleteComment(commentId: number) {
		const dialog = await alert.confirm("Delete comment", "Are you sure you want to delete this comment?")
		if (!dialog.result) return
		await trpc.community.deletePostComment.mutate(commentId)
		await Promise.all([post.refetch(), comments.refetch()])
		dialog.close()
	}

	const canReact = post.data != null && post.data.channel.userAccess !== "view"

	return (
		<>
			<PostReactions
				postId={isReactionsModalOpen ? lastPostId : null}
				onClose={() => setReactionsModalOpen(false)}
			/>
			<DefaultModal onClose={onClose} isOpen={postId !== null} width={800}>
				<ModalHeader>
					<ModalTitle>{post.data?.title ?? ""}</ModalTitle>
					<CloseButton onClick={onClose}>
						<StyledCloseIcon />
					</CloseButton>
				</ModalHeader>
				{lastPostId === null ? null : post.error ? (
					<ErrorText>Failed to load post.</ErrorText>
				) : post.data === undefined ? (
					<PartialLoadingPage />
				) : post.data === null ? (
					<ErrorText>Post was not found.</ErrorText>
				) : (
					<PostContent>
						{post.data.category && <p className="text-light mb-2 text-sm">{post.data.category.label}</p>}
						<Author>
							<CommunityProfileAvatar profile={post.data.profile} size={32} backgroundColor="white" />
							{post.data.profile.firstName} {post.data.profile.lastName}
							{post.data.profile.isCreator && (
								<IconShieldFilled className="size-4 text-indigo-500" title="Admin" />
							)}
							<Date title={toDateTimeString(post.data.createDate)}>
								{toRelativeDateString(post.data.createDate, "narrow")}
								{post.data.editDate && (
									<>
										<DotIcon />
										Edited {toRelativeDateString(post.data.editDate, "narrow")}
									</>
								)}
							</Date>
						</Author>
						<LexicalViewer html={post.data.htmlContent} className="mb-4" />
						{post.data.images.length > 0 && <PostImages images={post.data.images} />}
						{post.data.video !== null &&
							(post.data.video.type === "youtube" ? (
								<YoutubePlayer videoId={post.data.video.id} className="mb-4 max-w-xl rounded-lg" />
							) : post.data.video.type === "vimeo" ? (
								<VimeoPlayer videoId={post.data.video.id} className="mb-4 max-w-xl rounded-lg" />
							) : (
								exhaustiveGuard(post.data.video.type)
							))}
						{post.data.pollOptions.length > 0 && (
							<div className="mb-4 flex flex-col gap-2">
								{post.data.pollOptions.map(option => (
									<PollOption
										key={option.id}
										option={option}
										onChange={isSelected => (isSelected ? handleVote(option.id) : handleVote(null))}
									/>
								))}
							</div>
						)}
						{post.data.attachments.length > 0 && (
							<div className="mb-4 flex flex-wrap gap-2">
								{post.data.attachments.map(attachment => (
									<Attachment key={attachment.id} attachment={attachment} />
								))}
							</div>
						)}
						<Footer>
							<FooterItem>
								{(post.data.channel.userAccess === "react" ||
									post.data.channel.userAccess === "comment" ||
									post.data.channel.userAccess === "post") && (
									<CommunityPostReactionPicker
										value={post.data.userReactionType}
										onSelect={handleReact}
										className={post.data.userReactionType !== null ? "text-primary" : undefined}
									>
										<FooterIcon as={LikeIcon} />
									</CommunityPostReactionPicker>
								)}
								<Button onClick={() => setReactionsModalOpen(true)}>
									<CommunityPostReactionSummary counts={post.data.reactionCounts} />
								</Button>
							</FooterItem>
							<FooterItem>
								<FooterIcon as={CommentIcon} />
								{toCountFormat(post.data.commentsCount)}
							</FooterItem>
						</Footer>
						{comments.error ? (
							<p>Failed to load comments.</p>
						) : comments.data === undefined ? (
							<PartialLoadingPage />
						) : (
							comments.data.length > 0 && (
								<Comments>
									<CommunityCommentThreads
										compact
										key={post.data.id}
										comments={comments.data}
										canReply={
											post.data.channel.userAccess === "post" ||
											post.data.channel.userAccess === "comment"
										}
										onReply={async (parentId, text) => {
											if (!post.data) throw new Error("Post is no longer available")
											const result = await trpc.community.createPostComment.mutate({
												postId: post.data.id,
												parentId,
												text,
											})
											if (result.moderationStatus === "pending")
												void alert.show(
													"Awaiting approval",
													"Your reply will appear once approved.",
												)
											await Promise.all([post.refetch(), comments.refetch()])
										}}
									>
										{(comment, presentation) => (
											<Comment
												key={comment.id}
												comment={comment}
												{...presentation}
												canReact={canReact}
												onReact={type =>
													type !== null
														? setCommentReaction.mutate({ commentId: comment.id, type })
														: deleteCommentReaction.mutate(comment.id)
												}
												onDelete={() => handleDeleteComment(comment.id)}
											/>
										)}
									</CommunityCommentThreads>
								</Comments>
							)
						)}
						{(post.data.channel.userAccess === "post" || post.data.channel.userAccess === "comment") && (
							<CommunityCommentComposer
								key={post.data.id}
								value={comment}
								onChange={setComment}
								onSubmit={handleSubmitComment}
								isSending={createComment.isPending}
								label="Write a comment…"
								submitLabel="Post comment"
								sendingLabel="Sending…"
								errorLabel="Could not send your comment. Please try again."
							/>
						)}
					</PostContent>
				)}
			</DefaultModal>
		</>
	)
}

const PostImages: FC<{ images: { id: number; filePath: string }[] }> = ({ images }) => {
	const carousel = useImageCarousel(images)
	const visibleImages = images.slice(0, 4)
	const hiddenImageCount = images.length - visibleImages.length
	const overflowImageIndex = visibleImages.length - 1

	return (
		<div className="mb-4 grid max-w-2xl grid-cols-2 gap-2">
			{carousel.element}
			{visibleImages.map((image, index) => (
				<button
					key={image.id}
					type="button"
					onClick={() => carousel.showIndex(index)}
					className="relative w-full cursor-pointer"
					aria-label={
						index === overflowImageIndex && hiddenImageCount > 0
							? `View all ${images.length} post images`
							: undefined
					}
				>
					<img
						src={image.filePath}
						alt={`Post image ${index + 1}`}
						className="aspect-square w-full rounded-lg object-cover"
					/>
					{index === overflowImageIndex && hiddenImageCount > 0 && (
						<span
							aria-hidden="true"
							className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-black/50 text-xl font-semibold text-white"
						>
							+{hiddenImageCount}
						</span>
					)}
				</button>
			))}
		</div>
	)
}

const PostContent = styled.div`
	padding: 0 32px 24px;
	display: flex;
	flex-direction: column;
`

const CloseButton = styled.button`
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #7d7d7d;
	flex-shrink: 0;
`

const StyledCloseIcon = styled(CloseIcon)`
	width: 20px;
	height: 20px;
`

const ErrorText = styled.p`
	padding: 0 32px 24px;
	color: #757575;
`

const Author = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 8px;
`

const Date = styled.div`
	color: #949494;
	display: flex;
	align-items: center;
	gap: 8px;
`

const DotIcon = styled.div`
	width: 4px;
	height: 4px;
	background-color: #949494;
	border-radius: 50%;
`

const Footer = styled.div`
	display: flex;
	align-items: center;
	gap: 24px;
	padding: 12px 0 16px;
	border-top: 1px solid #dedede;
	margin-bottom: 16px;

	> * + * {
		border-left: 1px solid #dedede;
		padding-left: 24px;
	}
`

const FooterItem = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	font-family: ${fonts.mono};
`

const FooterIcon = styled.div`
	width: 20px;
	height: 20px;
`

const Comments = styled.div`
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	gap: 14px;
	margin-bottom: 16px;
`

export default PostDetail
